var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Expose, Type } from 'class-transformer';
import { ArrayMaxSize, ArrayMinSize, IsArray, IsBoolean, IsDateString, IsEnum, IsNumber, IsOptional, IsString, IsUUID, MaxLength, Min, ValidateNested, } from 'class-validator';
import { ExercisePerformanceType } from '../enums/exercise-performance-type.enum.js';
/**
 * How many exercises one printed page holds. The page layout is fixed (four
 * rows of equal height), so this is a layout constant, not a preference — the
 * renderer chunks the items by it and the template renders one page per chunk.
 */
export const EXERCISES_PER_PDF_PAGE = 4;
/**
 * Upper bound for a single print request. Every exercise costs two ffmpeg frame
 * extractions, so an unbounded basket would turn one request into minutes of
 * CPU. 40 exercises = 10 pages, far beyond any realistic training plan.
 */
export const MAX_EXERCISES_PER_PDF = 40;
/**
 * One row of the printed plan. Only the exercise reference and the therapist's
 * dosage travel from the client — title, description, device text and the
 * images are read server-side from the exercise itself, so the PDF can never
 * show anything the caller is not allowed to see.
 */
export class ExerciseProgramPdfItemDto {
    exerciseId;
    performanceType;
    performanceContent;
    setCount;
    pauseDuration;
    /** Weight in grams, as stored on the program item. */
    weight;
    /**
     * The therapist's note on this exercise ("Bemerkung"), printed under the
     * description.
     *
     * The one piece of prose that does travel from the client, and deliberately
     * so: it belongs to the program item rather than to the exercise, and in the
     * Programm it is not saved anywhere at the moment the plan is printed.
     * It is the caller's own words about their own patient — capped, like the
     * program item caps it, so a request cannot paste an essay onto the sheet.
     */
    customIndication;
}
__decorate([
    IsUUID(),
    Expose(),
    __metadata("design:type", String)
], ExerciseProgramPdfItemDto.prototype, "exerciseId", void 0);
__decorate([
    IsEnum(ExercisePerformanceType),
    Expose(),
    __metadata("design:type", String)
], ExerciseProgramPdfItemDto.prototype, "performanceType", void 0);
__decorate([
    IsNumber(),
    Min(0),
    Expose(),
    __metadata("design:type", Number)
], ExerciseProgramPdfItemDto.prototype, "performanceContent", void 0);
__decorate([
    IsNumber(),
    Min(0),
    Expose(),
    __metadata("design:type", Number)
], ExerciseProgramPdfItemDto.prototype, "setCount", void 0);
__decorate([
    IsNumber(),
    Min(0),
    Expose(),
    __metadata("design:type", Number)
], ExerciseProgramPdfItemDto.prototype, "pauseDuration", void 0);
__decorate([
    IsNumber(),
    Min(0),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], ExerciseProgramPdfItemDto.prototype, "weight", void 0);
__decorate([
    IsString(),
    IsOptional(),
    MaxLength(255),
    Expose(),
    __metadata("design:type", Object)
], ExerciseProgramPdfItemDto.prototype, "customIndication", void 0);
/**
 * The training frequency printed under the plan title. Carried in the request
 * because the plan is printed from whatever the therapist is looking at — a
 * saved program has a frequency, a basket that has not reached the assignment
 * step has none — and unlike the exercise texts nothing here can reveal
 * anything the caller could not already see.
 */
export class ExerciseProgramPdfFrequencyDto {
    trainingIsOnMonday;
    trainingIsOnTuesday;
    trainingIsOnWednesday;
    trainingIsOnThursday;
    trainingIsOnFriday;
    trainingIsOnSaturday;
    trainingIsOnSunday;
    /** ISO date; omitted for a program whose start date is not set yet. */
    startDate;
    /** ISO date; omitted for an open-ended program. */
    endDate;
}
__decorate([
    IsBoolean(),
    Expose(),
    __metadata("design:type", Boolean)
], ExerciseProgramPdfFrequencyDto.prototype, "trainingIsOnMonday", void 0);
__decorate([
    IsBoolean(),
    Expose(),
    __metadata("design:type", Boolean)
], ExerciseProgramPdfFrequencyDto.prototype, "trainingIsOnTuesday", void 0);
__decorate([
    IsBoolean(),
    Expose(),
    __metadata("design:type", Boolean)
], ExerciseProgramPdfFrequencyDto.prototype, "trainingIsOnWednesday", void 0);
__decorate([
    IsBoolean(),
    Expose(),
    __metadata("design:type", Boolean)
], ExerciseProgramPdfFrequencyDto.prototype, "trainingIsOnThursday", void 0);
__decorate([
    IsBoolean(),
    Expose(),
    __metadata("design:type", Boolean)
], ExerciseProgramPdfFrequencyDto.prototype, "trainingIsOnFriday", void 0);
__decorate([
    IsBoolean(),
    Expose(),
    __metadata("design:type", Boolean)
], ExerciseProgramPdfFrequencyDto.prototype, "trainingIsOnSaturday", void 0);
__decorate([
    IsBoolean(),
    Expose(),
    __metadata("design:type", Boolean)
], ExerciseProgramPdfFrequencyDto.prototype, "trainingIsOnSunday", void 0);
__decorate([
    IsDateString(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], ExerciseProgramPdfFrequencyDto.prototype, "startDate", void 0);
__decorate([
    IsDateString(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], ExerciseProgramPdfFrequencyDto.prototype, "endDate", void 0);
/** Request body for "Programm als PDF herunterladen". */
export class ExerciseProgramPdfRequestDto {
    /** Headline of the plan. Defaults to "Programm" when omitted. */
    title;
    /** Patient the plan was assembled for, printed next to the date. */
    patientName;
    /**
     * The office location whose letterhead, address and contact data head the
     * document. Omitted when the caller belongs to exactly one location — the
     * handler then resolves it.
     */
    officeLocationId;
    /**
     * Training days and runtime, printed as one line under the title. Left out
     * when no training day is set — an empty frequency is not printed at all.
     */
    frequency;
    items;
}
__decorate([
    IsString(),
    IsOptional(),
    MaxLength(120),
    Expose(),
    __metadata("design:type", String)
], ExerciseProgramPdfRequestDto.prototype, "title", void 0);
__decorate([
    IsString(),
    IsOptional(),
    MaxLength(120),
    Expose(),
    __metadata("design:type", String)
], ExerciseProgramPdfRequestDto.prototype, "patientName", void 0);
__decorate([
    IsUUID(),
    IsOptional(),
    Expose(),
    __metadata("design:type", String)
], ExerciseProgramPdfRequestDto.prototype, "officeLocationId", void 0);
__decorate([
    IsOptional(),
    ValidateNested(),
    Type(() => ExerciseProgramPdfFrequencyDto),
    Expose(),
    __metadata("design:type", ExerciseProgramPdfFrequencyDto)
], ExerciseProgramPdfRequestDto.prototype, "frequency", void 0);
__decorate([
    IsArray(),
    ArrayMinSize(1),
    ArrayMaxSize(MAX_EXERCISES_PER_PDF),
    ValidateNested({ each: true }),
    Type(() => ExerciseProgramPdfItemDto),
    Expose(),
    __metadata("design:type", Array)
], ExerciseProgramPdfRequestDto.prototype, "items", void 0);
//# sourceMappingURL=exercise-program-pdf-request.dto.js.map